<app-mobile-page-shell [hideBottomNav]="true">
  <div class="m-sage" [class.m-touring]="tourActive">
    <div class="alert-banner" *ngIf="loadError">
      <i class="bi bi-exclamation-circle"></i>
      <span>We couldn't load your Sage connection. Please refresh the page.</span>
    </div>

    <div class="paused-banner" *ngIf="!loadError && !syncEnabled">
      <i class="bi bi-pause-circle"></i>
      <span>Sync is paused for this organisation. The Sage connection is kept alive; no data is sent to or read from Sage.</span>
    </div>

    <ng-container *ngIf="!loadError">
      <section class="m-conn">
        <div class="m-conn-head">
          <div class="m-conn-title-block">
            <div class="m-conn-title">Sage Business Cloud Accounting</div>
            <div class="m-chips">
              <span class="chip connected">Connected</span>
              <span class="chip paused" *ngIf="!syncEnabled">Sync paused</span>
            </div>
          </div>
          <button type="button" class="help-btn" data-tour="help" [class.pulse]="helpPulse" (click)="helpRequested.emit()">
            <i class="bi bi-question-circle" aria-hidden="true"></i>Help
          </button>
          <button type="button" class="m-icon-btn" aria-label="Sage connection options" (click)="actionsOpen = true">
            <i class="bi bi-three-dots-vertical"></i>
          </button>
        </div>

        <!-- Stays mounted, so screen readers announce each step change and completion. -->
        <p class="m-conn-sub" aria-live="polite">{{ connSubtitle }}</p>

        <div class="m-progress" *ngIf="showSyncStepper">
          <progress [max]="syncProgress.steps.length" [value]="progressDone" aria-label="Sync progress"></progress>
          <button type="button" class="m-link-btn failed" *ngIf="syncProgress.anyFailed" (click)="tabSelected.emit('errors')">
            View errors
          </button>
        </div>

        <div class="m-conn-meta" *ngIf="lastSyncedAt">Last synced {{ lastSyncedAt | date: 'short' }}</div>

        <button type="button" class="btn btn-primary m-sync-all" [disabled]="!syncEnabled || !syncIdle" (click)="syncAll.emit()">
          <ng-container *ngIf="syncAllShowsSyncing; else syncAllIdle">
            <span class="sync-spinner on-dark" aria-hidden="true"></span>
            <i class="bi bi-hourglass-split sync-static-icon" aria-hidden="true"></i>Syncing…
          </ng-container>
          <ng-template #syncAllIdle><i class="bi bi-play-fill"></i>Sync all now</ng-template>
        </button>
      </section>

      <ng-container *ngIf="activeTab === 'sync'">
        <button type="button" class="m-summary-link" *ngIf="conflictBadgeCount" (click)="tabSelected.emit('conflicts')">
          <span><b>{{ conflictBadgeCount }}</b> conflict{{ conflictBadgeCount === 1 ? '' : 's' }} recorded</span>
          <i class="bi bi-chevron-right"></i>
        </button>

        <!-- Marks where the bar sits before it sticks; its position doesn't move when the bar does. -->
        <div #segmentAnchor class="m-segment-anchor" aria-hidden="true"></div>
        <div class="m-segment-bar">
          <div class="m-segment-track" role="tablist" aria-label="Dashboard sections">
            <button
              type="button"
              role="tab"
              class="m-seg"
              *ngFor="let s of dashSections; let i = index; trackBy: trackSection"
              [id]="'dash-seg-' + s.key"
              [class.active]="shownSection === s.key"
              [attr.aria-selected]="shownSection === s.key"
              [attr.tabindex]="shownSection === s.key ? 0 : -1"
              aria-controls="dash-panel"
              (click)="selectSection(s.key)"
              (keydown)="onSegmentKeydown($event, i)"
            >
              <span>{{ s.label }}</span>
              <span class="m-seg-count">{{ sectionCount(s.key) }}</span>
            </button>
          </div>
        </div>

        <div id="dash-panel" role="tabpanel" [attr.aria-labelledby]="'dash-seg-' + shownSection">
        <div class="m-health-stack" *ngIf="shownSection === 'health'">
          <div
            class="entity-card"
            *ngFor="let e of entityStatuses; trackBy: trackEntity"
            [attr.data-tour]="entitySlug(e.entityType)"
            [style.--card-accent]="entityAccent(e.entityType)"
            [class.is-syncing]="cardSyncState(e.entityType) === 'syncing'"
            [class.is-sync-done]="cardSyncState(e.entityType) === 'done'"
            [class.is-sync-failed]="cardSyncState(e.entityType) === 'failed'"
          >
            <div class="sync-shimmer" *ngIf="cardSyncState(e.entityType) === 'syncing'" aria-hidden="true"><span></span></div>
            <div class="entity-head">
              <div class="entity-icon" [style.background]="entityAccentSoft(e.entityType)" [style.color]="entityAccent(e.entityType)">
                <i class="bi" [ngClass]="entityIcon(e.entityType)"></i>
              </div>
              <div class="entity-name-block">
                <div class="entity-name">{{ entityLabel(e.entityType) }}</div>
                <div class="entity-dir">
                  {{ e.outbound ? syncDirectionLabel(e.outbound.direction) : (e.inbound ? syncDirectionLabel(e.inbound.direction) : 'Not yet synced') }}
                </div>
              </div>
              <ng-container [ngSwitch]="cardSyncState(e.entityType)">
                <span class="result-chip" *ngSwitchCase="'done'"><i class="bi bi-check-lg"></i>Done</span>
                <span
                  class="health-pill"
                  *ngSwitchDefault
                  [class.failed]="actionableCountFor(e.entityType) > 0 || cardSyncState(e.entityType) === 'failed'"
                >
                  {{ actionableCountFor(e.entityType) > 0
                      ? actionableCountFor(e.entityType) + ' to fix'
                      : cardSyncState(e.entityType) === 'failed' ? 'Sync failed' : 'Healthy' }}
                </span>
              </ng-container>
            </div>
            <div class="metrics">
              <div class="metric">
                <b>{{ e.outbound?.recordsSucceeded || 0 }}</b>
                <span>in sync</span>
              </div>
              <div class="metric pending">
                <b>{{ e.outbound?.recordsSkipped || 0 }}</b>
                <span>skipped</span>
              </div>
              <div class="metric errors">
                <b [class.has-err]="(e.outbound?.recordsFailed || 0) > 0">{{ e.outbound?.recordsFailed || 0 }}</b>
                <span>errors</span>
              </div>
            </div>
            <div class="entity-foot" [ngSwitch]="cardSyncState(e.entityType)">
              <span class="sync-pill" *ngSwitchCase="'syncing'">
                <span class="sync-spinner" aria-hidden="true"></span>
                <i class="bi bi-hourglass-split sync-static-icon" aria-hidden="true"></i>Syncing…
              </span>
              <span class="foot-result" *ngSwitchCase="'done'">Synced just now</span>
              <ng-container *ngSwitchCase="'failed'">
                <button type="button" class="m-link-btn failed" (click)="tabSelected.emit('errors')">View errors</button>
                <button class="btn btn-primary sync-btn" [disabled]="!syncEnabled || !syncIdle" (click)="syncEntity.emit(e.entityType)">
                  <i class="bi bi-play-fill"></i>Sync now
                </button>
              </ng-container>
              <ng-container *ngSwitchDefault>
                <span class="foot-when">
                  <i class="bi bi-clock-history"></i>
                  {{ e.outbound?.completedAt ? 'Synced ' + (e.outbound?.completedAt | date: 'short') : 'Not yet synced' }}
                </span>
                <button class="btn btn-primary sync-btn" [disabled]="!syncEnabled || !syncIdle" (click)="syncEntity.emit(e.entityType)">
                  <i class="bi bi-play-fill"></i>Sync now
                </button>
              </ng-container>
            </div>
          </div>
        </div>

        <app-sage-review-column
          *ngIf="shownSection === 'review'"
          [groups]="reviewGroups"
          [totalCount]="reviewTotalCount"
          [loading]="loadingReviewGroups"
          (openQueue)="tabSelected.emit('review')"
          (openGroup)="reviewGroupOpened.emit($event)"
        ></app-sage-review-column>

        <app-sage-errors-column
          *ngIf="shownSection === 'errors'"
          [groups]="errorGroups"
          [retryingCount]="retryingErrorCount"
          [loading]="loadingErrorGroups"
          (openIssues)="tabSelected.emit('errors')"
          (fixGroup)="errorGroupFixed.emit($event)"
        ></app-sage-errors-column>

        <app-sage-stock-column
          *ngIf="shownSection === 'stock'"
          [rows]="dashStockRows"
          [totalCount]="dashStockTotal"
          [reasons]="stockReasons"
          [syncEnabled]="syncEnabled"
          [loading]="loadingDashStock"
          (changed)="dashStockChanged.emit()"
          (viewAll)="tabSelected.emit('stock')"
          (syncDisabled)="syncDisabled.emit()"
        ></app-sage-stock-column>
        </div>
      </ng-container>

      <section class="m-panel" *ngIf="activeTab === 'runs'">
        <h2 class="m-panel-title">Recent sync runs</h2>

        <article class="m-card" *ngFor="let log of recentLogs">
          <div class="m-card-head">
            <span class="m-card-title">{{ syncRunEntityLabel(log.entityType) }}</span>
            <span class="result-pill success" *ngIf="syncStatusName(log.status) === 'Succeeded'">Success</span>
            <span class="result-pill partial" *ngIf="syncStatusName(log.status) === 'PartialFailure' || syncStatusName(log.status) === 'Failed'">
              Completed with errors
            </span>
            <span class="result-pill held" *ngIf="syncStatusName(log.status) === 'Running'">Running</span>
          </div>
          <div class="m-card-pills">
            <span
              class="dir-pill"
              [class.outbound]="syncDirectionName(log.direction) === 'Outbound'"
              [class.inbound]="syncDirectionName(log.direction) === 'Inbound'"
            >
              <i class="bi" [ngClass]="syncDirectionIcon(log.direction)"></i>{{ syncDirectionLabel(log.direction) }}
            </span>
            <span class="m-tag">{{ syncTriggerLabel(log.trigger) }}</span>
          </div>
          <dl class="m-fields">
            <dt>Records</dt>
            <dd>{{ log.recordsProcessed }} processed &middot; {{ log.recordsSucceeded }} succeeded</dd>
            <dt>Started</dt>
            <dd>{{ log.startedAt | date: 'short' }}</dd>
          </dl>
        </article>

        <p class="m-empty" *ngIf="loadingRuns && !recentLogs.length">Loading&hellip;</p>
        <p class="m-empty" *ngIf="!loadingRuns && !recentLogs.length">No sync runs yet.</p>

        <nav class="m-pager" *ngIf="runsTotalPages > 1" aria-label="Recent runs pages">
          <button type="button" [disabled]="runsPage <= 1 || loadingRuns" aria-label="Previous page" (click)="runsPageChange.emit(runsPage - 1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <span>Page {{ runsPage }} of {{ runsTotalPages }}</span>
          <button type="button" [disabled]="runsPage >= runsTotalPages || loadingRuns" aria-label="Next page" (click)="runsPageChange.emit(runsPage + 1)">
            <i class="bi bi-chevron-right"></i>
          </button>
        </nav>
      </section>

      <section class="m-panel" *ngIf="activeTab === 'errors'">
        <div class="m-drill-head">
          <button type="button" class="m-back-btn" aria-label="Back to dashboard" (click)="backToDashboard.emit()">
            <i class="bi bi-arrow-left"></i>
          </button>
          <div>
            <h2 class="m-panel-title">Sync errors</h2>
            <ng-container *ngIf="!showResolvedErrors">
              <p class="m-panel-sub" *ngIf="errorTotalCount">{{ errorFilteredCount }} of {{ errorTotalCount }} outstanding error(s)</p>
              <p class="m-panel-sub" *ngIf="!errorTotalCount">No outstanding errors — every blocked or rejected record has been resolved.</p>
            </ng-container>
            <ng-container *ngIf="showResolvedErrors">
              <p class="m-panel-sub" *ngIf="errorTotalCount">{{ errorFilteredCount }} of {{ errorTotalCount }} error(s), including resolved ones</p>
              <p class="m-panel-sub" *ngIf="!errorTotalCount">No sync errors have been recorded.</p>
            </ng-container>
          </div>
        </div>

        <div class="m-chips-row" role="group" aria-label="Reason" *ngIf="reasonChipsVisible">
          <button
            type="button"
            class="m-filter-chip"
            *ngFor="let c of errorCategoryCounts"
            [class.active]="selectedErrorCategories.includes(c.category)"
            [attr.aria-pressed]="selectedErrorCategories.includes(c.category)"
            [disabled]="loadingErrors"
            (click)="errorCategoryToggled.emit(c.category)"
          >
            {{ issueReasonLabel(c.category) }} <span class="m-filter-chip-count">{{ c.count }}</span>
          </button>
        </div>

        <input
          #errorQuery
          class="m-search"
          type="search"
          placeholder="Search by name, code or message…"
          aria-label="Search errors by name, code or message"
          autocomplete="off"
          [value]="errorSearch"
          (input)="errorSearchChange.emit(errorQuery.value)"
          (keydown.enter)="errorQuery.blur()"
        />

        <button
          type="button"
          class="m-toggle"
          [class.on]="showResolvedErrors"
          [attr.aria-pressed]="showResolvedErrors"
          [disabled]="loadingErrors"
          (click)="resolvedErrorsToggled.emit()"
        >
          <i class="bi" [class.bi-square]="!showResolvedErrors" [class.bi-check-square-fill]="showResolvedErrors"></i>Show resolved
        </button>

        <p class="m-live" aria-live="polite">
          {{ loadingErrors ? 'Loading errors…' : errorFilteredCount + ' error' + (errorFilteredCount === 1 ? '' : 's') + ' shown' }}
        </p>

        <article class="m-card m-issue" *ngFor="let row of errorRows; trackBy: trackIssue">
          <button type="button" class="m-issue-head" [attr.aria-expanded]="expandedIssueKey === row.key" (click)="issueRowToggled.emit(row)">
            <span class="entity-pill" [style.background]="entityAccentSoftFor(row.entityType)" [style.color]="entityAccentFor(row.entityType)">
              <i class="bi" [ngClass]="entityIconFor(row.entityType)"></i>{{ entityLabelFor(row.entityType) }}
            </span>
            <span class="m-card-title">{{ row.recordLabel }}</span>
            <span class="m-issue-message">{{ row.error?.errorMessage }}</span>
            <span class="m-issue-meta">
              <span class="result-pill success" *ngIf="row.error?.isResolved">Resolved</span>
              <span class="issue-reason" [class.issue-code-api]="row.reasonIsApiCode">{{ row.reason || '—' }}</span>
              <span>{{ row.detectedAt | date: 'dd MMM, HH:mm' }}</span>
              <i class="bi" [class.bi-chevron-down]="expandedIssueKey !== row.key" [class.bi-chevron-up]="expandedIssueKey === row.key"></i>
            </span>
          </button>

          <div class="m-issue-detail" *ngIf="expandedIssueKey === row.key && row.type === 'error' && row.error as err">
            <div class="m-detail-label">Error message</div>
            <pre class="m-payload">{{ err.errorMessage }}</pre>
            <ng-container *ngIf="err.hasResponseBody">
              <div class="m-detail-label">Sage's reply</div>
              <p class="m-hint" *ngIf="loadingErrorDetail">Loading…</p>
              <pre class="m-payload" *ngIf="expandedErrorDetail?.sageResponseBody">{{ expandedErrorDetail?.sageResponseBody }}</pre>
            </ng-container>
            <div class="m-detail-label">Record</div>
            <p class="m-hint">{{ err.recordLabel }} &middot; Source: {{ err.recordSource || '—' }}</p>
            <div class="m-detail-label">Sync run</div>
            <p class="m-hint">{{ entityLabelFor(row.entityType) }} &middot; {{ err.syncStartedAt | date: 'short' }}</p>
          </div>
        </article>

        <p class="m-empty" *ngIf="!loadingErrors && !errorRows.length">
          <i class="bi bi-check2-circle"></i>
          <span *ngIf="errorSearch.trim() || selectedErrorCategories.length">No errors match your filters.</span>
          <ng-container *ngIf="!errorSearch.trim() && !selectedErrorCategories.length">
            <span *ngIf="!showResolvedErrors">Nothing outstanding — every record has synced successfully.</span>
            <span *ngIf="showResolvedErrors">No sync errors have been recorded.</span>
          </ng-container>
        </p>

        <nav class="m-pager" *ngIf="errorTotalPages > 1" aria-label="Error pages">
          <button type="button" [disabled]="errorPage <= 1 || loadingErrors" aria-label="Previous page" (click)="errorPageChange.emit(errorPage - 1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <span>Page {{ errorPage }} of {{ errorTotalPages }}</span>
          <button type="button" [disabled]="errorPage >= errorTotalPages || loadingErrors" aria-label="Next page" (click)="errorPageChange.emit(errorPage + 1)">
            <i class="bi bi-chevron-right"></i>
          </button>
        </nav>

        <p class="m-note" *ngIf="errorRows.length">
          <i class="bi bi-info-circle"></i>
          <ng-container *ngIf="!showResolvedErrors">An error clears by itself once its record syncs. Fix the cause shown, then sync again.</ng-container>
          <ng-container *ngIf="showResolvedErrors">Errors marked Resolved have cleared because their record has synced since. They are kept as history.</ng-container>
        </p>
      </section>

      <section class="m-panel" *ngIf="activeTab === 'conflicts'">
        <h2 class="m-panel-title">Conflict log</h2>
        <p class="m-panel-sub" *ngIf="conflictFilteredCount">{{ conflictFilteredCount }} Sage change{{ conflictFilteredCount === 1 ? '' : 's' }} replaced by Aivor.</p>
        <p class="m-panel-sub" *ngIf="!conflictFilteredCount">No conflicts have been detected.</p>

        <select
          class="m-select"
          aria-label="Filter conflicts by record type"
          [ngModel]="selectedConflictEntityType"
          (ngModelChange)="conflictEntityTypeChange.emit($event)"
        >
          <option value="">All record types</option>
          <option *ngFor="let e of conflictEntityOptions" [value]="e.type">{{ e.label }}</option>
        </select>
        <input
          #conflictQuery
          class="m-search"
          type="search"
          placeholder="Search record id or name…"
          aria-label="Search conflicts by record id or name"
          autocomplete="off"
          [value]="conflictSearch"
          (input)="conflictSearchChange.emit(conflictQuery.value)"
          (keydown.enter)="conflictQuery.blur()"
        />

        <p class="m-live" aria-live="polite">
          {{ loadingConflicts ? 'Loading conflicts…' : conflictFilteredCount + ' conflict' + (conflictFilteredCount === 1 ? '' : 's') + ' shown' }}
        </p>

        <ng-container *ngFor="let row of conflictRows; trackBy: trackIssue">
          <article class="m-card m-conflict" *ngIf="row.conflict as c">
            <div class="m-card-head">
              <span class="entity-pill" [style.background]="entityAccentSoftFor(row.entityType)" [style.color]="entityAccentFor(row.entityType)">
                <i class="bi" [ngClass]="entityIconFor(row.entityType)"></i>{{ entityLabelFor(row.entityType) }}
              </span>
              <span class="won-pill">{{ winningSystemName(c.winningSystem) }} kept</span>
            </div>
            <div class="m-card-title">{{ row.recordLabel }}</div>
            <div class="m-hint">Detected {{ row.detectedAt | date: 'dd MMM yyyy, HH:mm' }}</div>
            <div class="m-conflict-field" *ngFor="let f of c.fields">
              <div class="code">{{ f.fieldName }}</div>
              <div class="m-pair">
                <span class="val-chip" [class.winner]="c.winningSystem === 1"><b>Aivor</b> {{ f.aivorValue || '—' }}</span>
                <span class="val-chip" [class.winner]="c.winningSystem === 2"><b>Sage</b> {{ f.sageValue || '—' }}</span>
              </div>
            </div>
            <!-- Without this, a conflict logged with no fields would show as an empty card. -->
            <p class="m-hint" *ngIf="!c.fields.length">No field-level detail was recorded.</p>
          </article>
        </ng-container>

        <p class="m-empty" *ngIf="!loadingConflicts && !conflictRows.length">
          <i class="bi bi-check2-circle"></i>
          <span *ngIf="conflictSearch.trim() || selectedConflictEntityType">No conflicts match your filters.</span>
          <span *ngIf="!conflictSearch.trim() && !selectedConflictEntityType">
            No conflicts have been detected — no Sage changes have been replaced by Aivor.
          </span>
        </p>

        <nav class="m-pager" *ngIf="conflictTotalPages > 1" aria-label="Conflict pages">
          <button type="button" [disabled]="conflictPage <= 1 || loadingConflicts" aria-label="Previous page" (click)="conflictPageChange.emit(conflictPage - 1)">
            <i class="bi bi-chevron-left"></i>
          </button>
          <span>Page {{ conflictPage }} of {{ conflictTotalPages }}</span>
          <button type="button" [disabled]="conflictPage >= conflictTotalPages || loadingConflicts" aria-label="Next page" (click)="conflictPageChange.emit(conflictPage + 1)">
            <i class="bi bi-chevron-right"></i>
          </button>
        </nav>

        <p class="m-note" *ngIf="conflictRows.length">
          <i class="bi bi-info-circle"></i>When a record was changed in both Aivor and Sage, Aivor's version is kept. Sage's changes are listed here so you can see them, but they aren't copied into Aivor.
        </p>
      </section>

      <section class="m-panel" *ngIf="activeTab === 'review'">
        <button type="button" class="m-back-link" (click)="backToDashboard.emit()"><i class="bi bi-arrow-left"></i>Back to dashboard</button>

        <div class="m-card m-review-empty" *ngIf="!reviewTotalCount">
          <i class="bi bi-check2-circle"></i>
          <div>
            <div class="m-card-title">Nothing is waiting for review</div>
            <div class="m-hint">Every record Sage has sent since onboarding has been applied.</div>
          </div>
        </div>

        <ng-container *ngIf="reviewTotalCount">
          <div class="m-card">
            <div class="m-card-title">{{ reviewTotalCount }} record{{ reviewTotalCount === 1 ? '' : 's' }} awaiting a decision</div>
            <div class="m-hint">
              {{ reviewEntityCount }} record type{{ reviewEntityCount === 1 ? '' : 's' }}<span *ngIf="oldestReviewAge"> · oldest waiting {{ oldestReviewAge }}</span>
            </div>
            <button type="button" class="btn btn-primary m-block-btn" [disabled]="applyBusy || !syncEnabled" (click)="applyAllRequested.emit()">
              <i class="bi bi-check2-all"></i>{{ applyingAllReview ? 'Applying…' : 'Apply reviewed records' }}
            </button>
          </div>

          <article class="m-card m-review-row" *ngFor="let r of reviewEntitiesOrdered">
            <div class="m-card-head">
              <span class="entity-icon" [style.background]="entityAccentSoft(r.entityType)" [style.color]="entityAccent(r.entityType)">
                <i class="bi" [ngClass]="entityIcon(r.entityType)"></i>
              </span>
              <span class="m-card-title">{{ entityLabel(r.entityType) }}</span>
              <span class="m-count"><b>{{ r.reviewCount }}</b> to review</span>
            </div>
            <div class="m-card-pills">
              <span class="m-tag" [class.stale]="isStale(r.sessionCreatedAt)" *ngIf="ageLabel(r.sessionCreatedAt)">
                <i class="bi bi-clock-history"></i>{{ ageLabel(r.sessionCreatedAt) }}
              </span>
              <span class="m-tag dep" *ngIf="dependencyPill(r)">{{ dependencyPill(r) }}</span>
            </div>
            <div class="m-actions">
              <button type="button" class="btn btn-primary" [disabled]="!entitySlug(r.entityType)" (click)="reviewOpened.emit(r)">
                Review<i class="bi bi-arrow-right"></i>
              </button>
              <button type="button" class="btn btn-secondary" [disabled]="applyBusy || !syncEnabled" (click)="reviewApplied.emit(r)">
                {{ applyingReviewSessionId === r.sessionId ? 'Applying…' : 'Apply' }}
              </button>
            </div>
          </article>

          <p class="m-hint" *ngIf="emptyEntityLabels.length">
            <i class="bi bi-check2-circle"></i>
            {{ emptyEntityLabels.join(', ') }} ha{{ emptyEntityLabels.length === 1 ? 's' : 've' }} nothing waiting.
          </p>
        </ng-container>
      </section>

      <section class="m-panel" *ngIf="activeTab === 'stock'">
        <div class="m-drill-head">
          <button type="button" class="m-back-btn" aria-label="Back to dashboard" (click)="backToDashboard.emit()">
            <i class="bi bi-arrow-left"></i>
          </button>
          <div>
            <h2 class="m-panel-title">Stock corrections</h2>
            <p class="m-panel-sub" *ngIf="stockBadgeCount">
              {{ stockBadgeCount }} item(s) hold different stock quantities in Aivor and Sage. Nothing is sent until you choose a reason.
            </p>
            <p class="m-panel-sub" *ngIf="!stockBadgeCount">Aivor and Sage agree on every linked item's stock quantity.</p>
          </div>
        </div>

        <article class="m-card" *ngFor="let a of stockAdjustments">
          <div class="m-card-head">
            <span class="m-card-title">{{ a.itemName || ('Item #' + a.itemId) }}</span>
            <span class="chip">{{ stockAdjustmentStatusName(a.status) }}</span>
          </div>
          <div class="m-hint code" *ngIf="a.itemCode">{{ a.itemCode }}</div>
          <div class="m-pair">
            <span class="val-chip" [class.winner]="a.direction === 1"><b>Aivor</b> {{ a.aivorQuantity }}</span>
            <span class="val-chip" [class.winner]="a.direction === 2"><b>Sage</b> {{ a.sageQuantity }}</span>
          </div>
          <dl class="m-fields">
            <dt>Correction</dt>
            <dd>{{ a.delta > 0 ? '+' : '' }}{{ a.delta }}</dd>
            <dt>Correct in</dt>
            <dd>{{ stockCorrectionTargetName(a.direction) }}</dd>
            <dt>Detected</dt>
            <dd>{{ a.detectedAt | date: 'short' }}</dd>
          </dl>
          <p class="m-hint" *ngIf="a.reasonDescription">{{ a.reasonDescription }}</p>
          <p class="m-hint" *ngIf="a.lastError">{{ a.lastError }}</p>
          <div class="m-actions" *ngIf="isStockAdjustmentOpen(a)">
            <button type="button" class="btn btn-primary" [disabled]="!syncEnabled" (click)="releaseRequested.emit(a)">Choose reason…</button>
            <button type="button" class="btn btn-secondary" (click)="stockAdjustmentCancelled.emit(a)">Dismiss</button>
          </div>
        </article>

        <p class="m-note" *ngIf="stockAdjustments.length">
          <i class="bi bi-info-circle"></i>One system is in charge of your stock count, and the other is corrected to match it. Both counts are checked again when you apply, so the correction always uses the latest numbers.
        </p>
      </section>

      <section class="m-panel m-fm" *ngIf="activeTab === 'matches' && fixMatches as fm">
        <div class="mk-toggle" role="group" aria-label="Records">
          <button
            type="button"
            *ngFor="let section of fixSections"
            [class.on]="fm.section === section"
            [attr.aria-pressed]="fm.section === section"
            (click)="fixSectionChange.emit(section)"
          >
            {{ fixSectionLabel(section, fm.sectionCounts[section]) }}
          </button>
        </div>

        <ng-container *ngIf="fm.section !== 'FinishedGood'; else finishedGoods">
          <div class="mk-tools">
            <label class="mk-search">
              <i class="bi bi-search" aria-hidden="true"></i>
              <input
                #fixQuery
                type="search"
                autocomplete="off"
                [placeholder]="fm.type === 'Item' ? 'Search by name or code' : 'Search by supplier name'"
                [attr.aria-label]="fm.type === 'Item' ? 'Search items' : 'Search suppliers'"
                [value]="fm.search"
                (input)="fixSearchChange.emit(fixQuery.value)"
                (keydown.enter)="fixQuery.blur()"
              />
            </label>
            <button
              type="button"
              class="mk-chip"
              *ngFor="let chip of fixChips(fm.type)"
              [class.on]="fm.chip === chip.key"
              [attr.aria-pressed]="fm.chip === chip.key"
              (click)="fixChipChange.emit(chip.key)"
            >
              {{ fixChipLabel(chip, fm.chipCounts.attention) }}
            </button>
          </div>

          <p class="m-hint" *ngIf="fm.loading && !fm.allRows.length">Loading&hellip;</p>
          <p class="m-warn" *ngIf="fm.loadError">
            <i class="bi bi-exclamation-triangle-fill"></i><span>Could not load the links. Open the tab again to retry.</span>
          </p>

          <article class="mk-card m-fm-card" *ngFor="let row of fm.rows; trackBy: trackLink" [class.m-card-fixed]="fm.fixed.has(row.aivorRecordId)">
            <div class="m-fm-head">
              <div>
                <b>{{ row.name }}</b>
                <div class="mk-code" *ngIf="row.code">{{ row.code }}</div>
              </div>
              <span class="mk-pill" *ngIf="fm.type === 'Item'" [ngClass]="linkTypePill(row)">{{ linkTypeLabel(row) }}</span>
            </div>
            <div class="mk-hr" *ngIf="fm.type === 'Item'"></div>
            <div class="mk-muted">Linked to Sage</div>
            <div class="m-fm-sage">{{ row.originalSageName ?? 'Added to Sage by Aivor' }}</div>
            <div class="m-fm-foot">
              <span class="mk-pill ok" *ngIf="fm.fixed.has(row.aivorRecordId); else notFixed">Fixed just now</span>
              <ng-template #notFixed>
                <span class="mk-pill warn" *ngIf="fm.attention.get(row.aivorRecordId) as issue; else syncedAt">{{ issue }}</span>
                <ng-template #syncedAt>
                  <span class="mk-muted">{{ row.lastSyncedAt ? relativeDayLabel(row.lastSyncedAt) : 'Not synced yet' }}</span>
                </ng-template>
              </ng-template>
              <button type="button" class="mk-btn" *ngIf="canChangeLink(row)" [disabled]="!syncEnabled" (click)="fixChangeOpened.emit(row)">Change</button>
            </div>
          </article>
          <p class="m-empty" *ngIf="!fm.loading && !fm.loadError && !fm.rows.length">{{ emptyLinksText(fm.type, fm.search, fm.chip) }}</p>
        </ng-container>

        <ng-template #finishedGoods>
          <h2 class="m-panel-title">How finished goods appear in Sage</h2>
          <p class="m-panel-sub">{{ targetCount }} finished good(s)</p>
          <!-- Desktop explains this in a title= tooltip, which touch can't reach. -->
          <p class="m-warn" *ngIf="unsetTargetCount > 0">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span>
              <b>{{ unsetTargetCount }} not saved as Bundle or Standalone.</b>
              They won't sync until you tap Save changes.
            </span>
          </p>

          <input
            #targetQuery
            class="m-search"
            type="search"
            placeholder="Search finished goods"
            aria-label="Search finished goods"
            autocomplete="off"
            [value]="targetsSearch"
            (input)="targetsSearchChange.emit(targetQuery.value)"
            (keydown.enter)="targetQuery.blur()"
          />

          <article class="m-card" *ngFor="let t of filteredTargets; trackBy: trackTarget">
            <div class="m-card-title">{{ t.itemName }}</div>
            <div class="m-hint">
              {{ t.categoryName }}<ng-container *ngIf="t.categoryName && t.itemCode"> &middot; </ng-container><span class="code" *ngIf="t.itemCode">{{ t.itemCode }}</span>
            </div>
            <div class="m-hint">Made from {{ t.componentCount }} item(s)</div>
            <p class="m-warn" *ngIf="isTargetBlocked(t)">
              <i class="bi bi-exclamation-triangle-fill"></i><span>{{ targetBlockedReason(t) }}</span>
            </p>

            <div class="m-target-row">
              <span class="toggle-label" [class.bundle]="t.target === 'Bundle'" [class.standalone]="t.target === 'Standalone'">{{ t.target }}</span>
              <button
                type="button"
                class="switch"
                role="switch"
                [class.on]="t.target === 'Bundle'"
                [attr.aria-checked]="t.target === 'Bundle'"
                [attr.aria-label]="'Sync ' + t.itemName + ' as a bundle'"
                (click)="targetToggled.emit(t)"
              >
                <span class="knob"></span>
              </button>
            </div>
            <p class="m-warn" *ngIf="!t.hasSavedTarget">
              <i class="bi bi-exclamation-triangle-fill"></i><span><b>Unsaved.</b> Not saved yet — tap Save changes below or it will not sync.</span>
            </p>

            <!-- prov-cell: the console's click-elsewhere handler keeps provenance open only for taps inside it. -->
            <div class="prov-cell">
              <button
                type="button"
                class="m-prov-trigger"
                [class.warn]="provenanceStateFor(t) === 'push-pending'"
                [attr.aria-expanded]="openProvenanceItemId === t.itemId"
                (click)="provenanceToggled.emit(t)"
              >
                <i class="bi bi-clock-history"></i>
                <ng-container [ngSwitch]="provenanceStateFor(t)">
                  <span *ngSwitchCase="'in-sync'">Synced</span>
                  <span *ngSwitchCase="'push-pending'">Not sent yet</span>
                  <span *ngSwitchCase="'never-synced'">Never synced</span>
                  <span *ngSwitchDefault>Last synced</span>
                </ng-container>
                <i class="bi" [class.bi-chevron-down]="openProvenanceItemId !== t.itemId" [class.bi-chevron-up]="openProvenanceItemId === t.itemId"></i>
              </button>
              <div class="m-prov" *ngIf="openProvenanceItemId === t.itemId">
                <p class="m-hint" *ngIf="provenanceFor(t) === 'loading'">Loading…</p>
                <p class="m-hint" *ngIf="provenanceFor(t) === 'error'">Could not load sync detail for this item.</p>
                <ng-container *ngIf="loadedProvenance(t) as p">
                  <dl class="m-fields">
                    <dt>Last synced with Sage</dt>
                    <dd>{{ p.sageLastSyncedAt ? (p.sageLastSyncedAt | date: 'short') : '- never synced' }}</dd>
                    <dt>Last sent to Sage</dt>
                    <dd>{{ p.sageLastPushedAt ? (p.sageLastPushedAt | date: 'short') : '- never sent' }}</dd>
                    <dt>Reference (for support)</dt>
                    <dd class="code">{{ p.sageLastPayloadHash || '-' }}</dd>
                  </dl>
                  <p class="m-hint" *ngIf="provenanceStateFor(t) === 'in-sync'">Up to date - nothing waiting to send.</p>
                  <p class="m-hint" *ngIf="provenanceStateFor(t) === 'push-pending'">Aivor's latest changes haven't reached Sage yet - check Sync errors for the reason.</p>
                  <p class="m-hint" *ngIf="provenanceStateFor(t) === 'never-synced'">This item hasn't been synced yet.</p>
                </ng-container>
              </div>
            </div>
          </article>

          <p class="m-empty" *ngIf="!filteredTargets.length">No finished goods match your search.</p>

          <div class="m-save-bar">
            <p class="m-hint">Bundle: Sage gets a bundle that lists the items it's made from. Standalone: Sage gets one item, with the cost and price of those items added together.</p>
            <div class="m-save-row">
              <span class="dirty-count" [class.dirty]="dirtyTargetCount > 0">
                {{ dirtyTargetCount > 0 ? dirtyTargetCount + ' unsaved change(s)' : 'All changes saved' }}
              </span>
              <button type="button" class="btn btn-primary" [disabled]="!dirtyTargetCount || savingTargets" (click)="targetsSaved.emit()">
                {{ savingTargets ? 'Saving…' : 'Save changes' }}
              </button>
            </div>
          </div>
        </ng-template>
      </section>

      <section class="m-panel" *ngIf="activeTab === 'groups'">
        <h2 class="m-panel-title">Sales and purchase groups</h2>
        <p class="m-panel-sub">{{ activeGroupCount }} active group(s) from Sage &middot; {{ inactiveGroupCount }} inactive</p>
        <p class="m-hint">Last refreshed {{ groupsLastSyncedAt ? (groupsLastSyncedAt | date: 'short') : 'Never' }}</p>

        <div class="m-actions">
          <button type="button" class="btn btn-secondary" (click)="setDefaultGroupRequested.emit()">
            <i class="bi bi-star"></i>Choose default sales group
          </button>
          <button type="button" class="btn btn-primary" [disabled]="refreshingGroups || !syncEnabled" (click)="groupsRefreshed.emit()">
            <i class="bi bi-arrow-repeat" [class.spin]="refreshingGroups"></i>{{ refreshingGroups ? 'Refreshing…' : 'Refresh from Sage' }}
          </button>
        </div>

        <div class="m-chips-row" role="group" aria-label="Type">
          <button type="button" class="m-filter-chip" [class.active]="groupTypeFilter === 0" [attr.aria-pressed]="groupTypeFilter === 0" (click)="groupTypeSelected.emit(0)">
            All <span class="m-filter-chip-count">{{ groupTypeCount(0) }}</span>
          </button>
          <button
            type="button"
            class="m-filter-chip"
            *ngFor="let t of reportGroupTypes"
            [class.active]="groupTypeFilter === t.id"
            [attr.aria-pressed]="groupTypeFilter === t.id"
            (click)="groupTypeSelected.emit(t.id)"
          >
            {{ t.label }} <span class="m-filter-chip-count">{{ groupTypeCount(t.id) }}</span>
          </button>
        </div>

        <button type="button" class="m-toggle" [class.on]="showInactiveGroups" [attr.aria-pressed]="showInactiveGroups" (click)="inactiveGroupsToggled.emit()">
          <i class="bi" [class.bi-square]="!showInactiveGroups" [class.bi-check-square-fill]="showInactiveGroups"></i>Show inactive
        </button>

        <input
          #groupQuery
          class="m-search"
          type="search"
          placeholder="Search group or Sage ID"
          aria-label="Search sales and purchase groups"
          autocomplete="off"
          [value]="groupSearch"
          (input)="groupSearchChange.emit(groupQuery.value)"
          (keydown.enter)="groupQuery.blur()"
        />

        <article class="m-card" *ngFor="let g of filteredGroups; trackBy: trackGroup" [class.inactive]="!g.isActive">
          <div class="m-card-head">
            <span class="m-card-title">{{ g.description }}</span>
            <span class="result-pill" [class.success]="g.isActive" [class.muted]="!g.isActive">{{ g.isActive ? 'Active' : 'Inactive' }}</span>
          </div>
          <span class="m-tag default" *ngIf="isDefaultSalesGroup(g)"><i class="bi bi-star-fill"></i>Default for items</span>
          <div class="m-hint">{{ g.isActive ? 'Group #' + g.id : 'No longer returned by Sage' }} &middot; Sage ID {{ g.sageItemReportingGroupId ?? '—' }}</div>
          <span
            class="entity-pill"
            [style.background]="reportGroupType(g.itemReportingGroupTypeId).bg"
            [style.color]="reportGroupType(g.itemReportingGroupTypeId).fg"
          >
            <i class="bi" [ngClass]="reportGroupType(g.itemReportingGroupTypeId).icon"></i>{{ reportGroupType(g.itemReportingGroupTypeId).label }}
          </span>
        </article>

        <p class="m-empty" *ngIf="!loadingGroups && !filteredGroups.length">No groups match your filters.</p>
        <p class="m-empty" *ngIf="loadingGroups">Loading groups…</p>

        <p class="m-note">
          <i class="bi bi-info-circle"></i>These groups are set up in Sage, and Aivor shows a copy here. Refresh from Sage to pick up new ones. A group
          removed in Sage shows here as inactive rather than being deleted, so past sales and purchases keep their group.
        </p>
      </section>
    </ng-container>
  </div>
</app-mobile-page-shell>

<!-- Outside the page shell, so the sheet's position: fixed isn't trapped by the shell's scroll box. -->
<app-offcanvas-sheet
  [visible]="actionsOpen"
  title="Sage connection"
  [showDefaultActions]="false"
  (closeCanvas)="actionsOpen = false"
>
  <button type="button" class="m-sheet-action danger" [disabled]="disconnecting" (click)="requestDisconnect()">
    <i class="bi bi-plug"></i>Disconnect Sage
  </button>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="showDisconnectConfirm"
  title="Disconnect Sage?"
  [showDefaultActions]="false"
  (closeCanvas)="disconnectCancelled.emit()"
>
  <p class="m-sheet-copy">
    Aivor will stop syncing with Sage until you reconnect. Your company link, matches and review
    history are kept — reconnecting brings you straight back to this console.
  </p>
  <div sheetActions class="m-sheet-actions">
    <button type="button" class="btn btn-secondary" (click)="disconnectCancelled.emit()">Cancel</button>
    <button type="button" class="btn btn-danger" [disabled]="disconnecting" (click)="disconnectConfirmed.emit()">Disconnect</button>
  </div>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="showSetDefaultGroupPrompt"
  title="Choose a sales group for your items"
  [saveLabel]="savingDefaultGroup ? 'Saving…' : 'Use this group'"
  cancelLabel="Not now"
  [saveDisabled]="!selectedDefaultGroupId || savingDefaultGroup"
  (save)="defaultGroupConfirmed.emit()"
  (closeCanvas)="defaultGroupCancelled.emit()"
>
  <p class="m-sheet-copy">
    In Sage, every item belongs to a sales group (Sage calls it a "sales reporting group"). It decides
    which income account in your Sage books that item's sales are recorded under.
  </p>
  <p class="m-sheet-copy">
    Your Aivor items don't have one yet, so Aivor can't send them to Sage until you choose.
  </p>
  <p class="m-sheet-copy">
    Pick the group most of your items belong under; your bookkeeper or accountant will know if you're
    not sure. Aivor uses it for every item that doesn't have its own group, and you can change it here later.
  </p>
  <select
    class="m-select"
    aria-label="Default sales group"
    [ngModel]="selectedDefaultGroupId"
    (ngModelChange)="selectedDefaultGroupIdChange.emit($event)"
  >
    <option [ngValue]="null" disabled>Choose a sales group…</option>
    <option *ngFor="let g of salesReportGroupOptions" [ngValue]="g.id">{{ g.description }}</option>
  </select>
  <!-- Desktop leaves this a dead end: Save stays disabled with nothing to pick and no reason given. -->
  <p class="m-hint" *ngIf="!salesReportGroupOptions.length">
    Sage hasn't sent any sales groups yet. Open the Groups tab and tap Refresh from Sage first.
  </p>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="showApplyAllConfirm"
  title="Apply reviewed records?"
  saveLabel="Apply reviewed records"
  [saveDisabled]="applyBusy || !syncEnabled"
  (save)="applyAllConfirmed.emit()"
  (closeCanvas)="applyAllCancelled.emit()"
>
  <p class="m-sheet-copy">
    Decisions you have made on {{ reviewEntityNames }} are locked in and a sync runs for each.
    Records you haven't decided on stay in the review, and nothing is sent to Sage for them
    until you decide.
  </p>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="!!pendingTargetSwitches"
  [title]="pendingTargetSwitches?.length === 1 ? 'Change how this is sent to Sage?' : 'Change how these are sent to Sage?'"
  saveLabel="Save changes"
  (save)="targetSwitchConfirmed.emit()"
  (closeCanvas)="targetSwitchCancelled.emit()"
>
  <p class="m-sheet-copy">{{ pendingTargetSwitches?.length === 1 ? 'This finished good is' : 'These finished goods are' }} already in Sage:</p>
  <ul class="m-sheet-list">
    <li *ngFor="let t of pendingTargetSwitches">{{ t.itemCode }} {{ t.itemName }}: {{ t.originalTarget }} → {{ t.target }}</li>
  </ul>
  <p class="m-sheet-copy">
    A bundle and an item are different records in Sage. The next sync creates the new one, or reuses one sent
    before, and makes the old one inactive in Sage. Any stock the old one holds stays on it in Sage.
  </p>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="!!releasingAdjustment"
  title="Why is this stock being corrected?"
  [saveLabel]="releasing ? 'Applying…' : 'Apply correction'"
  [saveDisabled]="!syncEnabled || !selectedReasonId || releasing"
  (save)="releaseConfirmed.emit()"
  (closeCanvas)="releaseCancelled.emit()"
>
  <!-- Projected content is built even while the sheet is hidden, so it needs its own guard. -->
  <ng-container *ngIf="releasingAdjustment as a">
    <p class="m-sheet-copy">
      {{ a.itemName || ('Item #' + a.itemId) }} will be adjusted by
      <strong>{{ a.delta > 0 ? '+' : '' }}{{ a.delta }}</strong>
      in {{ stockCorrectionTargetName(a.direction) }}.
    </p>
    <select
      class="m-select"
      aria-label="Reason for the correction"
      [ngModel]="selectedReasonId"
      (ngModelChange)="selectedReasonIdChange.emit($event)"
    >
      <option [ngValue]="null">Select a reason…</option>
      <option *ngFor="let r of stockReasons" [ngValue]="r.id">{{ r.description }}</option>
    </select>
  </ng-container>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="!!fixMatches?.changing"
  [title]="fixMatches?.type === 'Supplier' ? 'Change supplier link' : 'Change item link'"
  [showDefaultActions]="false"
  (closeCanvas)="fixChangeClosed.emit()"
>
  <app-fix-match-change
    *ngIf="fixMatches?.changing as changing"
    [type]="fixMatches!.type"
    [changing]="changing"
    [rows]="fixMatches!.allRows"
    [units]="fixMatches!.units"
    [saving]="fixMatches!.saving"
    [error]="fixMatches!.error"
    (move)="fixMoveSaved.emit($event)"
    (addAsNew)="fixAddSaved.emit($event)"
    (cancel)="fixChangeClosed.emit()"
  ></app-fix-match-change>
</app-offcanvas-sheet>

<app-offcanvas-sheet
  [visible]="!!errorFix"
  [title]="errorFix?.label ?? ''"
  [showDefaultActions]="false"
  (closeCanvas)="errorFixClosed.emit()"
>
  <app-error-fix-panel
    *ngIf="errorFix && fixStore"
    [group]="errorFix"
    [store]="fixStore"
    [unitFix]="unitFix"
    [syncEnabled]="syncEnabled"
    [syncBusy]="fixSyncBusy"
    [syncing]="fixSyncing"
    [synced]="fixSynced"
    [groupsLastRead]="groupsLastSyncedAt"
    idPrefix="phone-"
    (action)="errorFixAction.emit(errorFix)"
    (sync)="errorFixSync.emit($event)"
    (openMatching)="errorFixOpenMatching.emit($event)"
    (close)="errorFixClosed.emit()"
    (unitGroupChange)="unitFixGroupUnitChange.emit($event)"
    (unitItemChange)="unitFixUnitChange.emit($event)"
    (unitApply)="unitFixApplied.emit()"
  ></app-error-fix-panel>
</app-offcanvas-sheet>
